import type { ReactNode } from 'react';
import { Clock } from 'lucide-react';
import { useAuthStore } from '@/stores/auth';
import { useCountdown } from '@/hooks/use-countdown';
import { useTestLeadCount } from '@/hooks/use-test-lead-count';
import { useTestPhaseWatchdog } from '@/hooks/use-test-phase-watchdog';

function CountdownDisplay({ label }: { label: string }): ReactNode {
  return (
    <div className="flex flex-col gap-0.5">
      <div className="flex items-center gap-1.5">
        <Clock size={14} strokeWidth={2.25} className="text-brand-coral" />
        <p className="text-xs font-semibold uppercase tracking-[0.14em] text-brand-coral">
          Prueba final
        </p>
      </div>
      <p className="font-display text-2xl font-bold leading-none tabular-nums text-brand-red sm:text-3xl md:text-4xl">
        {label}
      </p>
    </div>
  );
}

type BarProps = { count: number; target: number; progress: number };

function ProgressBar({ count, target, progress }: BarProps): ReactNode {
  return (
    <div>
      <div className="mb-1.5 flex items-baseline justify-between gap-sm">
        <span className="text-xs tabular-nums text-brand-coral/85">
          <span className="font-semibold text-text-primary">{count}</span>
          {' / '}
          {target} creadores
        </span>
        <span className="text-xs font-semibold tabular-nums text-text-primary">
          {Math.round(progress)}%
        </span>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-brand-red/20">
        <div
          className="h-full rounded-full bg-brand-red shadow-[0_0_12px_rgba(255,68,68,0.5)] transition-all"
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
}

export function TestPhaseBanner(): ReactNode {
  const user = useAuthStore((s) => s.user);
  const countdown = useCountdown(user?.test_ends_at);
  const leads = useTestLeadCount();
  useTestPhaseWatchdog(user, countdown);

  if (user?.account_status !== 'test' || !countdown) return null;

  return (
    <div className="rounded-lg border border-brand-red/40 bg-brand-red/10 px-base py-md backdrop-blur-xl md:px-lg md:py-base">
      <div className="mx-auto flex max-w-7xl flex-col gap-md md:flex-row md:items-center md:justify-between md:gap-xl">
        <CountdownDisplay label={countdown.label} />
        {leads && (
          <div className="w-full min-w-0 md:max-w-[22rem] md:flex-1">
            <ProgressBar
              count={leads.count}
              target={leads.target}
              progress={leads.progress}
            />
          </div>
        )}
      </div>
    </div>
  );
}
